import { useTranslation } from 'react-i18next';
import type { LineItem } from '@pperform/pp-data-mappers';
import {
  getDisplayPrice,
  getDisplayQuantity,
  getDisplayProductDescription,
  getDisplayProductImageUrl,
  getProductID,
  getDisplayCustomReturnReason,
} from 'utils/lineItem.utils';
import { usePTPConfigContext } from 'GlobalConfig.context';
import { useCurrentShipment } from 'components/MerchantTrackingWidget/hooks/useCurrentShipment';
import { getNestField } from '../../../../../../../utils/object.utils';

export interface Option<T = string | number> {
  label: string;
  value: T;
}
interface ReturnValues {
  productName: string;
  productImageUrl?: string;
  productDescription?: Option<string>;
  productQuantity?: Option;
  productPrice?: Option;
  productUrl?: Option<string>;
  productReasonForReturn?: Option<string>;
  productCustomReasonForReturn?: Option<string>;
  additionalInformation: string;
  productID?: Option<string>;
}

const useFormatItemsCard = (card: LineItem): ReturnValues => {
  const { t } = useTranslation();
  const ptpConfig = usePTPConfigContext();
  const { shipment } = useCurrentShipment();
  const { lineItem: lineItemConfig, return: returnConfig } = ptpConfig;
  const { displayImages } = lineItemConfig;
  const { showReason } = returnConfig.returnDetails;

  const { isShow, customTitle, showUrl, showQuantity, showPrice } =
    lineItemConfig.productDescription;
  const {
    displayAdditionalLineItemFieldEnabled,
    displayAdditionalLineItemFields,
    displayProductIdEnabled,
  } = lineItemConfig;

  const showReasonForReturn =
    showReason && shipment.typeOfShipment === 'return';

  const showCustomReasonForReturn =
    showReasonForReturn && !!card.additionalInfo?.returnCustomReason;
  const showDescription = isShow && !showCustomReasonForReturn;

  const productDescription = {
    label: customTitle || t('LINE_ITEMS_PRODUCT_DESCRIPTION'),
    value: getDisplayProductDescription(card),
  };

  const productQuantity = {
    label: t('LINE_ITEMS_QUANTITY'),
    value: getDisplayQuantity(card),
  };

  const productPrice = {
    label: t('LINE_ITEMS_PRICE'),
    value: getDisplayPrice(card),
  };

  const productID = displayProductIdEnabled
    ? {
        label: 'ID',
        value: getProductID(card),
      }
    : null;

  const productUrl = {
    label: t('LINE_ITEMS_PRODUCT_URL'),
    value: card.productUrl,
  };

  const productImageUrl = getDisplayProductImageUrl(card, ptpConfig);

  const productReasonForReturn = {
    label: t('RETURN_REASON_FOR_RETURN'),
    value: t(getDisplayCustomReturnReason(card)),
  };

  const productCustomReasonForReturn = {
    label: t('SPECIFY'),
    value: card.additionalInfo?.returnCustomReason,
  };

  let additionalFields = [];

  if (displayAdditionalLineItemFieldEnabled) {
    additionalFields = additionalFields
      .concat(
        displayAdditionalLineItemFieldEnabled &&
          displayAdditionalLineItemFields?.map(
            (field) =>
              field.isEnabled &&
              getNestField(card.additionalInfo, field.fieldName),
          ),
      )
      .filter(Boolean);
  }

  const additionalInformation =
    additionalFields.join(' / ') ||
    (displayAdditionalLineItemFieldEnabled && ' ');

  return {
    productName: card.productName,
    ...(showDescription && { productDescription }),
    ...(showQuantity && { productQuantity }),
    ...(showPrice && { productPrice }),
    ...(showUrl && !!productUrl.value && { productUrl }),
    ...(displayImages && { productImageUrl }),
    ...(showReasonForReturn &&
      !!productReasonForReturn.value && { productReasonForReturn }),
    ...(showCustomReasonForReturn && { productCustomReasonForReturn }),
    additionalInformation,
    productID,
  };
};

export { useFormatItemsCard };
